import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  Type,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { enumHtsChatScope, enumHtsVerificationSource } from '@data-layer/enums/common';
import { HtsChatConversationModel, SimilarHtsSuggestionModel } from '@data-layer/models/common';
import { LineModel } from '@data-layer/models/entry-summaries';
import { HTS_ASSISTANT_ANSWER_MAX_LENGTH } from '../../const/hts-assistant.const';
import { enumHtsAssistantLock, enumHtsAssistantMode, enumHtsLineStatus } from '../../enums';
import { HtsAssistantHelper } from '../../helpers/hts-assistant.helper';
import { HtsLineStatusHelper } from '../../helpers/hts-line-status.helper';
import {
  IHtsAssistantHtsFound,
  IHtsAssistantInitConfig,
  IHtsAssistantLineRef,
  IHtsAssistantPanelInputs,
  IHtsAssistantViewModel,
  IHtsLineReason,
} from '../../interfaces';
import { HtsAssistantFacadeService } from '../../services/hts-assistant-facade.service';
import { HtsAssistantComposerComponent } from '../hts-assistant-composer/hts-assistant-composer.component';

/**
 * Classification assistant drawer (#13383). One component, three hosts: standalone from "Find HTS", the "Validate HTS
 * codes" view (#13384, `lines` + Back / Next + the line panel behind the eye button) and the read-only history
 * (#13385, `mode: Readonly`; the admin's and the Broker Review eye toggles the do-over).
 * A line without a description is classified right here: the opener asks for the product description. On the BINA Catalog
 * page (`isCatalogMode`, #13554) there is no line at all: the first message is the description, the result is saved to the
 * catalog only.
 * Hosts that create it dynamically drive line switches through `selectLine` / `setLines` (no template bindings there).
 */
@Component({
  selector: 'app-hts-assistant-drawer',
  templateUrl: './hts-assistant-drawer.component.html',
  styleUrls: ['./hts-assistant-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [HtsAssistantFacadeService],
})
export class HtsAssistantDrawerComponent implements OnInit, OnChanges, OnDestroy {
  private static reasonOf(line: IHtsAssistantLineRef): IHtsLineReason {
    return { key: line.reasonKey, params: line.reasonParams };
  }

  @Input() public mode: enumHtsAssistantMode = enumHtsAssistantMode.Live;
  /** `null` = the line is not saved yet. */
  @Input() public lineId: number | null = null;
  /** Session / pending-link key while the line has no id. */
  @Input() public invoiceId: number | null = null;
  @Input() public scope: enumHtsChatScope = enumHtsChatScope.Importer;
  /** Current form value, including unsaved edits. */
  @Input() public lineDescription: string | null = null;
  /** Current HTS of the line (digits or masked) — drives the "inserted" state of the result card. */
  @Input() public currentHts: string | null = null;
  /** The line's verification (hosts keep it current): a verified line without an active conversation shows the validated lock. */
  @Input() public verificationSource: enumHtsVerificationSource = enumHtsVerificationSource.None;
  @Input() public verificationMatchedText: string | null = null;
  /** The reason behind the verification (status chip texts): the card of the line's current code quotes it. */
  @Input() public verificationReason: IHtsLineReason | null = null;
  @Input() public companyId: number | null = null;
  /** Non-empty → Validate / History layout: progress + Back / Next, header count and the per-line status note. */
  @Input() public lines: IHtsAssistantLineRef[] = [];
  /** Validate: `lines` holds only the lines needing classification; the header counts against the whole entry. */
  @Input() public entryLinesCount: number | null = null;
  @Input() public selectedLineId: number | null = null;
  /** An embedded host may own the header. */
  @Input() public isShowHeader: boolean = true;
  /** Validate (#13384 F7) and standalone hosts: the es-mutual line panel (table row + full form) behind the eye; null → no eye. */
  @Input() public panelComponent: Type<unknown> | null = null;
  /** Validate view steps through its lines inside the panel (arrows); the standalone drawer shows one line. */
  @Input() public isPanelNavigation: boolean = true;
  /** Standalone host (no `lines`): the line behind the eye, kept current by the launcher. */
  @Input() public panelLineRef: IHtsAssistantLineRef | null = null;
  /** Do-over history (admin, Broker Review #13548): the "watch the do-over" toggle. */
  @Input() public isDoOverToggle: boolean = false;
  /** History: the entry-level read failed — nothing to show. */
  @Input() public isHistoryFailed: boolean = false;
  /** History: conversations per line from the entry-level read (no per-line requests). */
  @Input() public conversationsByLine: { [lineId: number]: HtsChatConversationModel[] } | null = null;
  @Input() public finalConversationIdByLine: { [lineId: number]: number | null } | null = null;
  /** BINA Catalog page (#13554): no line — the first message is the product description, no Insert, no lock. */
  @Input() public isCatalogMode: boolean = false;
  /** Catalog mode, reclassify of a withdrawn item: its description pre-filled in the composer. */
  @Input() public initialComposerText: string | null = null;
  /** Catalog item history (#13554): the item's conversations, read-only — no line, no lines map. */
  @Input() public presetConversations: HtsChatConversationModel[] | null = null;

  /** Same contract as `BinaSidebarComponent.htsFound`; `hts` is digits only. */
  @Output() public htsFound: EventEmitter<IHtsAssistantHtsFound> = new EventEmitter<IHtsAssistantHtsFound>();
  /** The line as returned by link / apply — hosts refresh their state from it. */
  @Output() public lineUpdated: EventEmitter<LineModel> = new EventEmitter<LineModel>();
  @Output() public savedToCatalog: EventEmitter<{ conversationId: number; hts: string }> = new EventEmitter<{
    conversationId: number;
    hts: string;
  }>();
  @Output() public close: EventEmitter<void> = new EventEmitter<void>();
  @Output() public lineSelected: EventEmitter<number> = new EventEmitter<number>();

  @ViewChild(HtsAssistantComposerComponent, { static: false })
  public composer: HtsAssistantComposerComponent;

  public vm: IHtsAssistantViewModel;
  public isPanelOpen: boolean = false;
  /** Reported by the line panel: the user has unsaved edits in its form. */
  public isPanelDirty: boolean = false;
  /** Reported by the line panel: its form is in edit mode — the assistant waits until the line is saved (#13384 F15). */
  public isPanelEditing: boolean = false;
  /** Close / line switch waiting for the "unsaved changes" answer. */
  public pendingAction: (() => void) | null = null;
  /** A new object on every line / lines change — `[dynamic-component]` re-binds inputs only on a reference change. */
  public panelInputs: IHtsAssistantPanelInputs | null = null;
  /** Stable reference: the directive re-subscribes to outputs only when it changes. */
  public readonly panelOutputs: { [key: string]: (event?: unknown) => void };

  /** Insert result of the selected line, handed to the line panel; reset on every line switch. */
  private appliedHts: IHtsAssistantHtsFound | null = null;
  private isDestroying: boolean = false;
  private isDestroy: Subject<void> = new Subject<void>();

  constructor(private facade: HtsAssistantFacadeService, private cdr: ChangeDetectorRef, private translateService: TranslateService) {
    this.panelOutputs = {
      prev: () => this.onPrev(),
      next: () => this.onNext(),
      close: () => this.closePanel(),
      saved: () => this.cdr.markForCheck(),
      dirtyChange: (isDirty: boolean) => {
        this.isPanelDirty = isDirty;
        this.cdr.markForCheck();
      },
      editModeChange: (isEditing: boolean) => {
        this.isPanelEditing = isEditing;
        // the panel is about to change the line (description, a backend insert): the chat waits until it is done.
        // The release is deferred: the panel also leaves edit mode as it is destroyed (children go before this drawer's
        // own ngOnDestroy), and nothing may start while the drawer closes.
        if (isEditing) {
          this.facade.setHostHold(true);
        } else {
          Promise.resolve().then(() => {
            if (!this.isDestroying) {
              this.facade.setHostHold(false);
            }
          });
        }
        this.cdr.markForCheck();
      },
    };
  }

  public ngOnInit(): void {
    this.openPanelIfDescriptionMissing();
    this.refreshPanelInputs();
    this.facade.vm$.pipe(takeUntil(this.isDestroy)).subscribe((vm: IHtsAssistantViewModel) => {
      this.vm = vm;
      this.cdr.markForCheck();
    });
    this.facade.htsFound$.pipe(takeUntil(this.isDestroy)).subscribe((found: IHtsAssistantHtsFound) => {
      this.appliedHts = found;
      this.refreshPanelInputs();
      this.htsFound.emit(found);
    });
    this.facade.lineUpdated$.pipe(takeUntil(this.isDestroy)).subscribe((line: LineModel) => this.lineUpdated.emit(line));
    this.facade.savedToCatalog$
      .pipe(takeUntil(this.isDestroy))
      .subscribe((saved: { conversationId: number; hts: string }) => this.savedToCatalog.emit(saved));

    this.facade.init(this.initConfig);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.lines || changes.selectedLineId) {
      this.refreshPanelInputs();
    }
    // `isCatalogMode` is not here: its host creates the drawer dynamically and sets it before the first init
    const isReinit: boolean = ['lineId', 'mode', 'scope', 'selectedLineId'].some(
      (key: string) => changes[key] && !changes[key].firstChange,
    );
    if (isReinit) {
      this.facade.init(this.initConfig);
      return;
    }
    // verification first: a description change decides on auto-start with the line's current source in hand
    const isVerificationChanged: boolean = ['verificationSource', 'verificationMatchedText', 'verificationReason'].some(
      (key: string) => changes[key] && !changes[key].firstChange,
    );
    if (isVerificationChanged) {
      this.facade.setVerification(this.verificationSource, this.verificationMatchedText, this.reasonText(this.verificationReason));
    }
    if (changes.lineDescription && !changes.lineDescription.firstChange) {
      this.facade.setLineDescription(this.lineDescription);
    }
    if (changes.currentHts && !changes.currentHts.firstChange) {
      this.facade.setCurrentHts(this.currentHts);
    }
  }

  public ngOnDestroy(): void {
    this.isDestroying = true;
    this.isDestroy.next();
    this.isDestroy.complete();
  }

  // ---- template flags ----------------------------------------------------------------------------

  public get titleKey(): string {
    return this.vm.isReadonly ? 'HTS_ASSISTANT.HISTORY_TITLE' : 'HTS_ASSISTANT.TITLE';
  }

  public get isShowReadonlyChip(): boolean {
    return this.vm.isReadonly && !this.vm.isLoading;
  }

  public get isLinesMode(): boolean {
    return this.lines.length > 0;
  }

  /** Progress + "Item N of M" on top, Back / Next at the bottom (Validate and History alike). */
  public get isShowStepper(): boolean {
    return this.lines.length > 0;
  }

  public get isShowPanelEye(): boolean {
    return !this.vm.isReadonly && !!this.panelComponent;
  }

  /** Standalone layout has no progress row: the eye sits at the right of the product line. */
  public get isShowProductEye(): boolean {
    return this.isShowPanelEye && !this.isShowStepper;
  }

  public get isShowPanel(): boolean {
    return this.isPanelOpen && !!this.panelComponent && !!this.panelInputs;
  }

  public get panelEyeLabelKey(): string {
    return this.isPanelOpen ? 'HTS_ASSISTANT.LINE_PANEL_HIDE' : 'HTS_ASSISTANT.LINE_PANEL_SHOW';
  }

  public get isShowUnsavedModal(): boolean {
    return this.pendingAction !== null;
  }

  public get isShowQuickQuestionsHint(): boolean {
    return this.isShowStepper && !this.vm.isReadonly && !this.vm.description;
  }

  /** The chat is held: no saved description, an unsaved description in the host form, or the panel is being edited. */
  public get isAssistantLocked(): boolean {
    return !this.vm.isReadonly && (this.vm.lockReason !== null || this.isPanelEditing);
  }

  /** The "validated" / "no answer earlier" locks are informational and offer "Start a conversation". */
  public get isVerifiedLock(): boolean {
    return (
      !this.isPanelEditing &&
      (this.vm.lockReason === enumHtsAssistantLock.Validated || this.vm.lockReason === enumHtsAssistantLock.NoAnswerEarlier)
    );
  }

  public get lockTitleKey(): string {
    if (!this.isVerifiedLock) {
      return 'HTS_ASSISTANT.LOCK_TITLE';
    }
    return this.vm.lockReason === enumHtsAssistantLock.NoAnswerEarlier
      ? 'HTS_ASSISTANT.LOCK_NO_ANSWER_TITLE'
      : 'HTS_ASSISTANT.LOCK_VALIDATED_TITLE';
  }

  public get lockTextKey(): string {
    if (this.isPanelEditing) {
      return 'HTS_ASSISTANT.LOCK_EDITING';
    }
    switch (this.vm.lockReason) {
      case enumHtsAssistantLock.DraftChanged:
        return 'HTS_ASSISTANT.LOCK_DRAFT_CHANGED';
      case enumHtsAssistantLock.NoAnswerEarlier:
        return 'HTS_ASSISTANT.LOCK_NO_ANSWER_EARLIER';
      case enumHtsAssistantLock.Validated:
        return this.vm.verificationSource === enumHtsVerificationSource.SimilarityConfirmed
          ? 'HTS_ASSISTANT.LOCK_SIMILARITY'
          : 'HTS_ASSISTANT.LOCK_VALIDATED';
      default:
        return this.panelComponent ? 'HTS_ASSISTANT.LOCK_NO_DESCRIPTION_PANEL' : 'HTS_ASSISTANT.LOCK_NO_DESCRIPTION_FORM';
    }
  }

  public get lockTextParams(): { [key: string]: string } {
    return { code: HtsAssistantHelper.formatHtsNumber(this.vm.currentHts), matchedText: this.vm.verificationMatchedText || '' };
  }

  /** The line has no saved description: the panel opens in edit mode with the field flagged. */
  public get isPanelForceEdit(): boolean {
    return !HtsAssistantHelper.normalizeDescription(this.lineDescription);
  }

  public get selectedIndex(): number {
    return this.lines.findIndex((line: IHtsAssistantLineRef) => line.lineId === this.selectedLineId);
  }

  public get isPrevDisabled(): boolean {
    return this.vm.isBusy || this.selectedIndex <= 0;
  }

  public get isNextDisabled(): boolean {
    return this.vm.isBusy || this.selectedIndex < 0 || this.selectedIndex >= this.lines.length - 1;
  }

  public get isShowDoOverButton(): boolean {
    return this.isDoOverToggle && this.vm.isDoOverAvailable;
  }

  public get doOverTooltipKey(): string {
    return this.vm.isShowDoOver ? 'HTS_ASSISTANT.RETURN_TO_FINAL' : 'HTS_ASSISTANT.WATCH_DO_OVER';
  }

  /** History with nothing to show: the entry has no conversations at all, or the read failed. */
  public get isShowHistoryEmpty(): boolean {
    return this.isHistoryHost && this.lines.length === 0;
  }

  /** The "Product — …" row quotes the line's description; catalog mode has no line — a history shows the conversation's own. */
  public get isShowProductRow(): boolean {
    return !this.isShowHistoryEmpty && (!this.isCatalogMode || (this.vm.isReadonly && !!this.vm.description));
  }

  /** Per-line empty state; the entry-level one (no lines at all) already says it. */
  public get isShowEmptyReadonly(): boolean {
    return this.vm.isEmptyReadonly && !this.isShowHistoryEmpty;
  }

  public get historyEmptyKey(): string {
    return this.isHistoryFailed ? 'HTS_ASSISTANT.NOTICE.TRY_AGAIN' : 'HTS_ASSISTANT.HISTORY_EMPTY';
  }

  /** A catalog item's history (#13554) has no line item to name. */
  public get emptyReadonlyKey(): string {
    return this.isCatalogMode ? 'HTS_ASSISTANT.NO_CONVERSATIONS_CATALOG' : 'HTS_ASSISTANT.NO_CONVERSATIONS';
  }

  public get selectedLine(): IHtsAssistantLineRef | null {
    return this.lines.find((line: IHtsAssistantLineRef) => line.lineId === this.selectedLineId) || null;
  }

  public get needsClassificationCount(): number {
    return this.lines.filter(HtsLineStatusHelper.isNeedsClassificationRef).length;
  }

  public get headerTotal(): number {
    return this.entryLinesCount !== null ? this.entryLinesCount : this.lines.length;
  }

  public get isShowHeaderCount(): boolean {
    return this.isLinesMode && !this.vm.isReadonly;
  }

  public get isAllValidated(): boolean {
    return this.needsClassificationCount === 0;
  }

  /** Validate and History alike (#13384 / #13385): the selected line's status chip with its reason. */
  public get isShowStatusNote(): boolean {
    return this.isLinesMode && !!this.selectedLine;
  }

  public get isSelectedLineRemoved(): boolean {
    return !!this.selectedLine && this.selectedLine.isRemoved === true;
  }

  /** The status row already names where the verification came from (#13533); a removed line's row does not. */
  public get isHideCarriedNotes(): boolean {
    return this.isShowStatusNote && !this.isSelectedLineRemoved;
  }

  public get isSelectedLineValidated(): boolean {
    return !!this.selectedLine && this.selectedLine.status === enumHtsLineStatus.Validated;
  }

  public get isShowNotice(): boolean {
    return !!this.vm.notice;
  }

  /** The footer holds the composer; Start over lives in the thread. */
  public get isShowFooter(): boolean {
    return !this.vm.isReadonly && !this.vm.isLoading && this.vm.isShowComposer;
  }

  public get suggestionsToggleKey(): string {
    return this.vm.isSuggestionsOverlay ? 'HTS_ASSISTANT.SUGGESTIONS_HIDE' : 'HTS_ASSISTANT.SUGGESTIONS_SHOW';
  }

  /** With the line's current code on the step, it offers to replace that code or to consult the assistant (2026-10-16). */
  public get isVerifiedSuggestionsStep(): boolean {
    return !!this.vm.currentSuggestion;
  }

  /** Dismiss waits for a confirm in flight too: the facade ignores it then, so the button says so. */
  public get isDismissDisabled(): boolean {
    return this.vm.isBusy || this.vm.confirmingHts !== null;
  }

  public get suggestionsHintKey(): string {
    return this.isVerifiedSuggestionsStep ? 'HTS_ASSISTANT.SUGGESTIONS_HINT_VERIFIED' : 'HTS_ASSISTANT.SUGGESTIONS_HINT';
  }

  public get suggestionsDismissKey(): string {
    return this.isVerifiedSuggestionsStep ? 'HTS_ASSISTANT.SUGGESTIONS_DISMISS_VERIFIED' : 'HTS_ASSISTANT.SUGGESTIONS_DISMISS';
  }

  /** The composer stays visible under the lock overlay (design) but cannot be used. */
  public get isComposerDisabled(): boolean {
    return this.vm.isBusy || this.isAssistantLocked;
  }

  public get composerMaxLength(): number {
    return HTS_ASSISTANT_ANSWER_MAX_LENGTH;
  }

  /** Catalog mode, no conversation to answer: the composer asks for the product description. */
  public get composerPlaceholderKey(): string {
    return this.vm.isCatalogStart ? 'HTS_ASSISTANT.COMPOSER_PLACEHOLDER_CATALOG' : 'HTS_ASSISTANT.COMPOSER_PLACEHOLDER';
  }

  // ---- host API (dynamically created hosts set inputs imperatively, so ngOnChanges does not run) ----

  /** The SAVED description changed (host reload). */
  public setLineDescription(description: string | null): void {
    this.lineDescription = description;
    this.facade.setLineDescription(description);
  }

  /** Standalone host: live form text — the chat waits for Find HTS while it differs from the saved description. */
  public setDraftDescription(description: string | null): void {
    this.facade.setDraftDescription(description);
  }

  /** Standalone host: the line's verification after an entry reload. */
  public setVerification(
    source: enumHtsVerificationSource | null | undefined,
    matchedText: string | null | undefined,
    reason: IHtsLineReason | null = null,
  ): void {
    this.verificationSource = source || enumHtsVerificationSource.None;
    this.verificationMatchedText = matchedText || null;
    this.verificationReason = reason;
    this.facade.setVerification(this.verificationSource, this.verificationMatchedText, this.reasonText(reason));
    this.cdr.markForCheck();
  }

  /** Validate / History hosts: switch the drawer to another line of the entry. */
  public selectLine(line: IHtsAssistantLineRef): void {
    this.lineId = line.lineId;
    this.invoiceId = line.invoiceId;
    this.selectedLineId = line.lineId;
    this.lineDescription = line.description;
    this.currentHts = line.htsNumber;
    this.verificationSource = line.verificationSource;
    this.verificationMatchedText = line.matchedText;
    this.verificationReason = HtsAssistantDrawerComponent.reasonOf(line);
    this.appliedHts = null;
    this.openPanelIfDescriptionMissing();
    this.refreshPanelInputs();
    this.facade.init(this.initConfig);
    this.cdr.markForCheck();
  }

  /** Standalone host: the line behind the eye after every entry reload. */
  public setPanelLineRef(lineRef: IHtsAssistantLineRef | null): void {
    this.panelLineRef = lineRef;
    this.refreshPanelInputs();
    this.cdr.markForCheck();
  }

  /** Validate / History hosts: the entry was reloaded — refresh statuses and the selected line's code / description. */
  public setLines(lines: IHtsAssistantLineRef[], entryLinesCount: number | null = null): void {
    this.lines = lines;
    this.entryLinesCount = entryLinesCount;
    const selected: IHtsAssistantLineRef | null = this.selectedLine;
    if (selected) {
      this.lineDescription = selected.description;
      this.currentHts = selected.htsNumber;
      this.verificationSource = selected.verificationSource;
      this.verificationMatchedText = selected.matchedText;
      this.verificationReason = HtsAssistantDrawerComponent.reasonOf(selected);
      // code, then verification (its tariff lookup reads the code), then description (its auto-start reads the source)
      this.facade.setCurrentHts(selected.htsNumber);
      this.facade.setVerification(selected.verificationSource, selected.matchedText, this.reasonText(this.verificationReason));
      this.facade.setLineDescription(selected.description);
    }
    this.refreshPanelInputs();
    this.cdr.markForCheck();
  }

  // ---- handlers ----------------------------------------------------------------------------------

  public onSend(text: string): void {
    // the pre-filled description (reclassify) is for the first message only: a composer re-created later starts empty
    this.initialComposerText = null;
    this.facade
      .send(text)
      .pipe(takeUntil(this.isDestroy))
      .subscribe(
        () => {
          if (this.composer) {
            this.composer.focus();
          }
        },
        () => {
          // the notice is already in the view model; give the user their text back to retry
          if (this.composer) {
            this.composer.setValue(text);
            this.composer.focus();
          }
        },
      );
  }

  public onStartOver(): void {
    this.facade.restart();
  }

  /** The validated lock's button: a conversation about an already verified line, on the user's request. */
  public onStartConversation(): void {
    this.facade.startConversation();
  }

  public onDismissSuggestions(): void {
    this.facade.dismissSuggestions();
  }

  public onToggleSuggestions(): void {
    this.facade.toggleSuggestions();
  }

  public onConfirmSuggestion(suggestion: SimilarHtsSuggestionModel): void {
    this.facade.confirmSuggestion(suggestion);
  }

  public trackBySuggestion(index: number, suggestion: SimilarHtsSuggestionModel): string {
    return `${suggestion.htsNumber}-${suggestion.source}`;
  }

  public onInsert(conversationId: number): void {
    this.facade.insert(conversationId);
  }

  public onSaveToCatalog(conversationId: number): void {
    this.facade.saveToCatalog(conversationId);
  }

  public onDismissNotice(): void {
    this.facade.dismissNotice();
  }

  public togglePanel(): void {
    if (this.isPanelOpen) {
      this.closePanel();
      return;
    }
    this.isPanelOpen = true;
  }

  public closePanel(): void {
    this.guardUnsaved(() => {
      this.isPanelOpen = false;
      this.cdr.markForCheck();
    });
  }

  /** "Stay and save" (also Esc / backdrop): nothing happens, Save stays available. */
  public onUnsavedStay(): void {
    this.pendingAction = null;
  }

  /** "Continue without saving": run what was asked; the panel re-reports once its form is gone. */
  public onUnsavedLeave(): void {
    const action: (() => void) | null = this.pendingAction;
    this.pendingAction = null;
    this.isPanelDirty = false;
    if (action) {
      action();
    }
  }

  public onPrev(): void {
    if (!this.isPrevDisabled) {
      const lineId: number = this.lines[this.selectedIndex - 1].lineId;
      this.guardUnsaved(() => this.lineSelected.emit(lineId));
    }
  }

  public onNext(): void {
    if (!this.isNextDisabled) {
      const lineId: number = this.lines[this.selectedIndex + 1].lineId;
      this.guardUnsaved(() => this.lineSelected.emit(lineId));
    }
  }

  public onToggleDoOver(): void {
    this.facade.toggleDoOver();
  }

  public onClose(): void {
    this.guardUnsaved(() => this.close.emit());
  }

  /** History hosts always hand the conversations map over; standalone read-only openings (broker, locked entry) do not. */
  private get isHistoryHost(): boolean {
    return this.conversationsByLine !== null;
  }

  private get initConfig(): IHtsAssistantInitConfig {
    return {
      mode: this.mode,
      scope: this.scope,
      lineId: this.lineId,
      invoiceId: this.invoiceId,
      companyId: this.companyId,
      lineDescription: this.lineDescription,
      currentHts: this.currentHts,
      verificationSource: this.verificationSource,
      verificationMatchedText: this.verificationMatchedText,
      verificationReasonText: this.reasonText(this.verificationReason),
      isHostHeld: this.isPanelEditing,
      isCatalogMode: this.isCatalogMode,
      ...this.historyInitConfig,
    };
  }

  /** History hosts hand the conversations over (a line's with the do-over's final one; a catalog item's) — no per-line read. */
  private get historyInitConfig(): Partial<IHtsAssistantInitConfig> {
    if (this.presetConversations !== null) {
      return { conversations: this.presetConversations, historyFinalConversationId: null };
    }
    if (!this.conversationsByLine || this.lineId === null) {
      return {};
    }
    const finalIds: { [lineId: number]: number | null } = this.finalConversationIdByLine || {};
    return {
      conversations: this.conversationsByLine[this.lineId] || [],
      historyFinalConversationId: finalIds[this.lineId] === undefined ? null : finalIds[this.lineId],
    };
  }

  /** The status reason as the title of the current code's card; a generic title when the host knows no reason. */
  private reasonText(reason: IHtsLineReason | null): string {
    return reason
      ? this.translateService.instant(reason.key, reason.params || undefined)
      : this.translateService.instant('HTS_ASSISTANT.CURRENT_CODE_TITLE');
  }

  /** Unsaved edits in the open line panel: ask first (F12); otherwise act at once. */
  private guardUnsaved(action: () => void): void {
    if (this.isPanelOpen && this.isPanelDirty) {
      this.pendingAction = action;
      this.cdr.markForCheck();
      return;
    }
    action();
  }

  private refreshPanelInputs(): void {
    const selected: IHtsAssistantLineRef | null = this.selectedLine || this.panelLineRef;
    const index: number = this.selectedIndex;
    this.panelInputs = selected
      ? {
          lineRef: selected,
          isFirst: !this.isPanelNavigation || index <= 0,
          isLast: !this.isPanelNavigation || index >= this.lines.length - 1,
          applied: this.appliedHts,
          isForceEdit: this.isPanelForceEdit,
          isNavigable: this.isPanelNavigation,
        }
      : null;
  }

  /**
   * Validate view on a line without a description: the panel is where the description gets written, so open it.
   * The panel's edit state is PREDICTED here (it reports the same value a moment later, mid change detection): a forced
   * line is in edit mode as soon as the panel shows it, any other line starts in read mode.
   */
  private openPanelIfDescriptionMissing(): void {
    if (this.panelComponent && this.mode === enumHtsAssistantMode.Live && this.isPanelForceEdit) {
      this.isPanelOpen = true;
    }
    this.isPanelEditing = this.isPanelOpen && this.isPanelForceEdit;
  }
}
